import { Box, Text } from '@wix/design-system';
import { SparklesFilledSmall } from '@wix/wix-ui-icons-common';
import React, { useContext } from 'react';

import { RicosContext } from './../../../ricos-context';
import type { TFunction } from './../../../ricos-types';

import { AI_MODAL_RESULTS_CONTENT_ITEM } from '../../data-hooks';
import { unreachable } from '../../ts-utils';
import type { Entry, Prompt, ResultEntry, ResultsActionsCollection } from '../../types';
import { ResultContentViewer } from '../result-content/result-content';
import { ResultsFooter } from '../results-footer/results-footer';
import { TextCursor } from '../text-cursor/text-cursor';
import styles from './feed-scss';

type FeedProps = {
  entries: Entry[];
  zIndex: number;
  getResultActions: (entry: ResultEntry) => ResultsActionsCollection;
  isLoading?: boolean;
  modalRef: React.RefObject<any | null>;
  feedUserEntryFormatter?: (entry: string | Prompt, t: TFunction) => string;
};

const defaultFeedUserEntryFormatter = (prompt: string | Prompt, t: TFunction) => {
  return typeof prompt === 'string' ? prompt : t(prompt.title);
};

export const Feed: React.FC<FeedProps> = ({
  entries,
  zIndex,
  getResultActions,
  isLoading,
  modalRef,
  feedUserEntryFormatter = defaultFeedUserEntryFormatter,
}) => {
  const { t } = useContext(RicosContext);

  return (
    <Box direction="vertical">
      {entries.map((entry, index) => {
        switch (entry.type) {
          case 'request': {
            return (
              <Box
                key={index}
                direction="vertical"
                paddingLeft="SP4"
                paddingRight="SP4"
                paddingTop="SP2"
                paddingBottom="SP2"
                gap="SP1"
              >
                <Text
                  size="small"
                  weight="bold"
                  tagName="h6"
                  className={styles.label}
                  skin="disabled"
                >
                  {t('AIPlugin_FeedLabel_You')}
                </Text>
                <Text size="small" tagName="p" className={styles.entry} skin="disabled">
                  {feedUserEntryFormatter(entry.requestPrompt, t)}
                </Text>
              </Box>
            );
          }
          case 'result': {
            return (
              <Box
                direction="vertical"
                paddingTop="SP2"
                backgroundColor={entries.length - 1 === index ? 'B60' : undefined}
                dataHook={AI_MODAL_RESULTS_CONTENT_ITEM}
              >
                <ResponseBodyEntry key={index}>
                  <ResultContentViewer resultItem={entry.response} />
                </ResponseBodyEntry>

                <ResultsFooter
                  actions={getResultActions(entry)}
                  entry={entry}
                  zIndex={zIndex}
                  modalRef={modalRef}
                />
              </Box>
            );
          }
          default: {
            throw unreachable(entry);
          }
        }
      })}
      {isLoading && (
        <Box direction="vertical" paddingTop="SP2">
          <ResponseBodyEntry>
            <Box direction="vertical" paddingBottom="SP3">
              <TextCursor />
            </Box>
          </ResponseBodyEntry>
        </Box>
      )}
    </Box>
  );
};

const ResponseBodyEntry: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { t } = useContext(RicosContext);

  return (
    <Box direction="vertical" paddingLeft="SP4" paddingRight="SP4" gap="SP1">
      <Box verticalAlign="middle">
        <Text size="small" weight="bold" tagName="h6" className={styles.label}>
          {t('AIPlugin_FeedLabel_Assistant')}
        </Text>
        <Box color="B10">
          <SparklesFilledSmall />
        </Box>
      </Box>
      {children}
    </Box>
  );
};
